import React, { useState, useRef, useEffect } from 'react';
import { SOCKET_URL } from '../../config/api';
import './EditBrandModal.css';

const EditBrandModal = ({ isOpen, onClose, onSave, brand }) => {
  const [brandName, setBrandName] = useState('');
  const [brandLogo, setBrandLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (brand) {
      setBrandName(brand.name || '');
      setBrandLogo(null);
      setLogoPreview(brand.logo ? `${SOCKET_URL}${brand.logo}` : null);
      setError('');
      setSaving(false);
    }
  }, [brand]);

  if (!isOpen || !brand) return null;

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBrandLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleCancel = () => {
    setBrandName('');
    setBrandLogo(null);
    setLogoPreview(null);
    setError('');
    onClose();
  };

  const handleSave = async () => {
    if (!brandName || !brandName.trim()) {
      setError('Please enter a brand name');
      return;
    }

    if (onSave) {
      setSaving(true);
      setError('');
      try {
        await onSave(brand.id, {
          name: brandName,
          logo: brandLogo,
        });
        setSaving(false);
      } catch (err) {
        console.error('Edit brand error:', err);
        setError(err.response?.data?.error || err.message || 'Failed to update brand');
        setSaving(false);
      }
    }
  };

  const initials = brand.name ? brand.name.charAt(0).toUpperCase() : '?';

  return (
    <div className="edit-brand-modal-overlay" onClick={onClose}>
      <div className="edit-brand-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="edit-brand-modal-header">
          <h2 className="edit-brand-modal-title">Edit Brand</h2>
        </div>

        <div className="edit-brand-modal-content">
          {/* Brand Logo Upload */}
          <div className="edit-brand-logo-section">
            <div className="edit-brand-logo-wrapper" onClick={handleLogoClick}>
              {logoPreview ? (
                <img
                  src={logoPreview}
                  alt="Brand logo preview"
                  className="edit-brand-logo-preview"
                  onError={() => setLogoPreview(null)}
                />
              ) : (
                <div className="edit-brand-logo-placeholder">
                  <span className="edit-brand-logo-initial">{initials}</span>
                </div>
              )}
              <div className="edit-brand-logo-overlay">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 5V19M5 12H19" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span>Change</span>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleLogoChange}
                accept="image/*"
                className="edit-brand-logo-input"
              />
            </div>
            <p className="edit-brand-logo-hint">Click to upload or change logo</p>
          </div>

          {/* Brand Name Input */}
          <div className="edit-brand-input-section">
            <label className="edit-brand-input-label">Brand name</label>
            <div className="edit-brand-input-wrapper">
              <input
                type="text"
                placeholder="Brand Name"
                value={brandName}
                onChange={(e) => { setBrandName(e.target.value); setError(''); }}
                className="edit-brand-input"
                disabled={saving}
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div style={{ color: '#e74c3c', fontSize: '13px', marginTop: '4px', padding: '0 4px', alignSelf: 'flex-start' }}>
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="edit-brand-actions">
            <button
              type="button"
              className="edit-brand-btn edit-brand-btn-cancel"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="edit-brand-btn edit-brand-btn-save"
              onClick={handleSave}
              disabled={saving}
              style={saving ? { opacity: 0.7 } : {}}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditBrandModal;
